/**
 * Small API helper shared across the storefront — base URL resolution (same rules
 * PortalContext.js already used) plus the customer-facing quote-request submission this
 * business's "Request a Quote" flow needs. See docs/modules.md "Quote requests" in the platform
 * monorepo this template is deployed from.
 */

// Live API when not on localhost; no hardcoded dev data in production
export function getApiBaseUrl() {
  if (typeof window === 'undefined') return process.env.REACT_APP_API_URL || 'https://api.mango-business.solutions/api/v1';
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  if (isLocal) return process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
  return process.env.REACT_APP_API_URL || 'https://api.mango-business.solutions/api/v1';
}

/**
 * Submit a product-based quote request (print job) — public endpoint, no login required.
 * `payload`: { jobDescription, guestName, guestEmail, guestPhone, photos?, urgency?, budget? }
 */
export async function submitProductQuoteRequest(productId, payload) {
  const response = await fetch(`${getApiBaseUrl()}/products/${productId}/quote-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.error || 'Failed to submit quote request');
  }
  return data.data;
}

/**
 * Submit a service-based quote request (e.g. Screen Printing / Digital Printing) — public
 * endpoint, no login required. Same payload shape as the product version.
 */
export async function submitServiceQuoteRequest(serviceId, payload) {
  const response = await fetch(`${getApiBaseUrl()}/services/${serviceId}/quote-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.error || 'Failed to submit quote request');
  }
  return data.data;
}

/**
 * Upload one piece of customer artwork for the Design & Order page — public endpoint, no login
 * required. Returns a real hosted URL (R2) suitable for storing in a QuoteRequest's designData.
 */
export async function uploadDesignArtwork(file, businessId) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('businessId', businessId);
  const response = await fetch(`${getApiBaseUrl()}/design-uploads`, {
    method: 'POST',
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.error || 'Failed to upload artwork');
  }
  return data.url;
}
